import React, { useState, useEffect, useMemo } from "react";
import {
  Button as BsButton,
  InputGroup,
  Form,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import ManagementHeader from "../Components/ManagementHeader";
import { DocumentCard } from "../Components/DocumentLibrary/DocumentCard";
import { useParams } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "../../context/useUserStore";
import { toast } from "react-toastify";
import localization from "services/localization";
import { useLanguageStore } from "context/useLanguageStore";
import { usePromptsStore } from "context/usePromptsStore";
import { usePromptCategoriesStore } from "context/usePromptCategoriesStore";
import CustomSelect from "views/Components/CustomSelect";

export default function DocumentHistory() {
  const { currentLang } = useLanguageStore();
  const { moduleId, departmentId } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState({
    value: "all",
    label: `${localization.all_prompts || "All Prompts"}`,
  });

  const { appUser, userData } = useUserStore();
  const { prompts } = usePromptsStore();
  const { promptCategories } = usePromptCategoriesStore();

  const stripHTMLForTitle = (html) => {
    if (!html) return "";
    // Remove <head> completely
    let cleaned = html.replace(/<head[\s\S]*?<\/head>/gi, "");
    // Remove remaining tags
    cleaned = cleaned.replace(/<[^>]+>/g, "");
    // Collapse spaces
    cleaned = cleaned.replace(/\s+/g, " ").trim();
    return cleaned;
  };

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = async () => {
    try {
      setLoading(true);

      const historyParams =
        appUser?.role === "student"
          ? { department_id: departmentId, privacy: "public" }
          : { department_id: departmentId, user_id: appUser?.id };

      const historiesRes = await SpecificAPI.getAllHistory(
        historyParams,
        userData?.selected_school_id,
      );

      const historyIds = new Set((historiesRes || []).map((h) => h.id));

      const messagesRes = await SpecificAPI.getAllMessages(
        {
          is_editable: true,
          user_id: appUser?.id,
        },
        userData?.selected_school_id,
      );

      const filteredMessages = (messagesRes || []).filter((msg) =>
        historyIds.has(msg.history_id),
      );

      debugger;
      const docs = filteredMessages.map((doc) => {
        const raw = doc.updated_response || doc.response || "";

        return {
          id: doc.id,
          historyId: doc.history_id,
          title: stripHTMLForTitle(raw).slice(0, 50) + "...",
          tag: "Generale",
          content: raw,
          createdAt: doc.updated_at || doc.created_at,
          response: doc.response,
          updated_response: doc.updated_response,
          promptId: doc.prompt_id,
        };
      });

      setDocuments(docs);
    } catch (err) {
      console.error("Failed to load history messages:", err);
      toast.error("Failed to load history messages.");
    } finally {
      setLoading(false);
    }
  };

  const administrativePromptOptions = useMemo(() => {
    const baseOption = {
      value: "all",
      label: localization.all_prompts || "All Prompts",
    };

    const categoryMap = new Map(
      (promptCategories || []).map((category) => [category.id, category]),
    );

    const administrativePrompts = (prompts || []).filter((prompt) => {
      const category =
        categoryMap.get(prompt.prompt_category_id) ||
        categoryMap.get(prompt.promptCategoryId);

      return (
        category?.type?.toLowerCase() === "administration" ||
        category?.type?.toLowerCase() === "administration"
      );
    });

    const mappedPrompts = administrativePrompts.map((prompt) => ({
      value: prompt.id,
      label:
        prompt.title?.[currentLang] ||
        prompt.title?.It ||
        prompt.title?.En ||
        "Unnamed Prompt",
    }));

    return [baseOption, ...mappedPrompts];
  }, [prompts, promptCategories, currentLang]);

  const handlePromptChange = (selected) => {
    setSelectedPrompt(
      selected || {
        value: "all",
        label: localization.all_prompts || "All Prompts",
      },
    );
  };

  // Filter documents based on search and selected tag
  const filteredDocuments = documents.filter((doc) => {
    const title = doc.title || "";
    const content = stripHTMLForTitle(doc.content || "");

    const matchesSearch =
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPrompt =
      selectedPrompt?.value === "all" ||
      String(doc.promptId) === String(selectedPrompt?.value);

    return matchesSearch && matchesPrompt;
  });

  return (
    <div className="d-flex flex-column w-100" style={{ height: "100%" }}>
      <div
        className="d-flex flex-column"
        style={{
          height: "calc(100vh - 65px)",
          backgroundColor: "var(--dashboard-bg)",
          color: "var(--main-text-color)",
          overflow: "hidden",
        }}
      >
        <div
          className="px-4 pt-4 pb-3"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            backgroundColor: "var(--dashboard-bg)",
            borderBottom: "1px solid var(--border-color, rgba(0,0,0,0.08))",
            flexShrink: 0,
          }}
        >
          <ManagementHeader
            title={localization.documents_history || "Documents History"}
            subtitle={
              localization.manage_your_documents || "Manage your documents"
            }
            showBack={true}
            backTo={`/prompt-categories/${moduleId}/${departmentId}`}
            showAdd={false}
          />

          <Row className="w-100 mt-4 g-3 align-items-center">
            <Col xs={12} md={7}>
              <InputGroup className="search-field">
                <InputGroup.Text
                  id="search-addon"
                  className="customSearchAddon"
                >
                  <FaSearch className="icon-btn" />
                </InputGroup.Text>
                <Form.Control
                  placeholder={
                    localization.search_by_document_title ||
                    "Search by document title..."
                  }
                  aria-label="Search"
                  aria-describedby="search-addon"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="customSearchInput"
                />
              </InputGroup>
            </Col>

            <Col xs={12} md={5}>
              <CustomSelect
                options={administrativePromptOptions}
                value={selectedPrompt}
                onChange={handlePromptChange}
                placeholder={localization.select_prompt || "Select prompt"}
              />
            </Col>
          </Row>

          {/* Tag Filters */}
          <div className="d-flex gap-2 mt-3 flex-wrap">
            <Badge
              bg="primary"
              style={{ color: "white" }}
              className="text-xs font-semibold px-3 py-2 rounded-full flex-shrink-0"
            >
              {localization.all || "Tutti"} ({filteredDocuments.length})
            </Badge>
          </div>
        </div>

        {/* Documents Grid */}
        <div
          className="flex-grow-1 px-4 pb-4"
          style={{
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <ClipLoader size={40} color="var(--primary-icon)" />
            </div>
          ) : (
            <div className="row g-4 mt-3 justify-content-start">
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((document) => (
                  <div key={document.id} className="col-12 col-md-6 col-lg-4">
                    <DocumentCard
                      document={document}
                      onEdit={() => console.log("Edit", document)}
                      onDelete={() => console.log("Delete", document.id)}
                    />
                  </div>
                ))
              ) : (
                <p className="text-center text-muted">
                  {localization.no_documents_found || "No documents found."}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
